import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react'
import { listEntries, type FinancialEntry } from '@/lib/services/billing'

interface BillingContextType {
  entries: FinancialEntry[]
  loading: boolean
  error: string | null
  fetchEntries: (unitId: string, dateFrom: string, dateTo: string) => Promise<void>
  invalidateEntries: () => void
}

const BillingContext = createContext<BillingContextType | undefined>(undefined)

export function BillingProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<FinancialEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cacheKeyRef = useRef<string>('')
  const lastParamsRef = useRef<{ unitId: string; dateFrom: string; dateTo: string } | null>(null)

  /**
   * Fetch entries for a given date range.
   * Results are cached by (unitId, dateFrom, dateTo) key.
   */
  const fetchEntries = useCallback(
    async (unitId: string, dateFrom: string, dateTo: string) => {
      const cacheKey = `${unitId}:${dateFrom}:${dateTo}`
      if (cacheKey === cacheKeyRef.current) {
        // Already loaded for this params, skip refetch
        return
      }

      setLoading(true)
      setError(null)
      try {
        const data = await listEntries(unitId, {
          dueDateFrom: dateFrom,
          dueDateTo: dateTo,
        })
        setEntries(data)
        cacheKeyRef.current = cacheKey
        lastParamsRef.current = { unitId, dateFrom, dateTo }
      } catch (err) {
        console.error('[BillingContext] Error fetching entries:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
        setEntries([])
      } finally {
        setLoading(false)
      }
    },
    []
  )

  /**
   * Invalidate the cache so fetchEntries will refetch next time.
   * Call this after creating, updating, or deleting entries.
   */
  const invalidateEntries = useCallback(() => {
    // Clear the cache key so next fetch will refetch.
    cacheKeyRef.current = ''
    // If we have previous params, immediately refetch to update consumers.
    const p = lastParamsRef.current
    if (p) {
      // Trigger refetch by temporarily clearing the cache key then fetching
      setLoading(true)
      listEntries(p.unitId, {
        dueDateFrom: p.dateFrom,
        dueDateTo: p.dateTo,
      })
        .then(data => {
          setEntries(data)
          cacheKeyRef.current = `${p.unitId}:${p.dateFrom}:${p.dateTo}`
          setError(null)
          // Notify UI (TopBar) to reload billing aggregates/badges
          try { window.dispatchEvent(new Event('reloadBilling')) } catch (e) { /* noop */ }
        })
        .catch(err => {
          console.error('[BillingContext] Error refetching entries:', err)
          setError(err instanceof Error ? err.message : 'Unknown error')
        })
        .finally(() => setLoading(false))
    }
  }, [])

  return (
    <BillingContext.Provider
      value={{ entries, loading, error, fetchEntries, invalidateEntries }}
    >
      {children}
    </BillingContext.Provider>
  )
}

/**
 * Hook to access billing context.
 * Must be used within BillingProvider.
 */
export function useBilling() {
  const ctx = useContext(BillingContext)
  if (!ctx) {
    throw new Error('useBilling must be used within BillingProvider')
  }
  return ctx
}
